/* eslint-disable arrow-body-style */
import { xAxisHighLight, xAxisLabelFormat } from "@/pages/dashboard/components/Chart/_config/plug";
import { EChartsOption } from "echarts-for-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { handleAxisFormat, setXAxis, setYAxisLabelFormatter, setYAxisMax, setSeriesStack, setSeriesEmphasis, applyYAxisConfig, getPieData } from "./_utils";
import { metricMaxValueMap } from "./_constant";
import { DATE_OR_TIME_AXIS_TYPE, SMALL_VAL_METRIC, TIME_TYPE_MAP } from "../../../config/constant";
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import PieChart from '@/pages/dashboard/components/Chart/components/PieChart';
import { useWeekDayArr } from "@/pages/dashboard/utils/customHook";
import { chartTooltiosFormatter } from "@/pages/dashboard/components/Chart/_config/utils";
import { useIntl } from "umi";
import { LANGUAGE_EN } from "@home/_config/config";
import AITable from "../AITable";
import { TMetricObj } from "../TabComponent/_type";
import { TXAxis, TYAxisItem } from "../AIContent/_type";
import SvgIcon from "@home/components/SvgIcon";
import dateTimeFormat, { isMomentParsableString } from "@home/utils/dbDataFormat/dateTimeFormat";
import { agentDataFormat, agentYAxisFormat } from "@/pages/aigc/utils/utils";
import { useAgentStoreContext } from "../../hooks/useLocalState";
import { getEchartTooltipText } from "@/pages/dashboard/components/EchartsBubble";

type ChartType = 'line' | 'bar' | 'flat_bar' | 'table' | 'pie';
type ChatMode = 'trend' | 'line' | 'bar' | 'table' | 'pie';

// list 项接口：支持通过 icon 传入任意 ReactNode（如 <img>），不传则降级到 SvgIcon
interface IRenderListItem {
  type: ChartType;
  svgType: string;
  content: string;
  component: React.ReactNode;
  icon?: React.ReactNode;
}

// useRenderList 返回类型
interface IRenderListResult {
  renderList: IRenderListItem[];
  defaultActiveTab: number;
  defaultSelected?: string;
}

// 2. 预定义配置映射（替换原defaultActiveMap）
const CHART_MODE_CONFIG: Record<ChatMode, {
  allowedTypes: ChartType[];
  defaultIndex: number;
}> = {
  trend: {
    allowedTypes: ['line', 'bar', 'pie', 'table'],
    defaultIndex: 0
  },
  line: {
    allowedTypes: ['line', 'pie', 'table'],
    defaultIndex: 0
  },
  bar: {
    allowedTypes: ['bar', 'pie', 'table'],
    defaultIndex: 0
  },
  table: {
    allowedTypes: ['table'],
    defaultIndex: 1
  },
  pie: {
    allowedTypes: ['pie', 'table'],
    defaultIndex: 0
  },
};

interface IProps {
  // 筛选器的值
  filterVal: { [key: string]: string; };
  // 指标对象
  metricObj: TMetricObj;
  // y轴格式化
  yAxisFormatter: string;
  xAxis: TXAxis;
  componentData: {
    chart: string[];
  };
  xAxisData: string[];
  legendData: [];
  tableInfo: { [key: string]: string; };
  // 浮窗格式化
  toolTipsFormatter: string;
  height: number;
  lineSeries: any[];
  barSeries: any[];
  // 图表类型list
  chatType: string | string[];
  isCopilot?: boolean;
  // 浮窗时间格式化
  toolTipsDateFormatter: string;
  // 图表数据id
  chartDataId?: string;
  // yAxis 配置（包含所有 ECharts yAxis 配置项）
  yAxisConfig?: TYAxisItem | null;
  isFullScreen?: boolean;
}

export const useRenderList = (props: IProps) => {
  const { filterVal, metricObj, yAxisFormatter, chatType: propsChatType, xAxis, componentData, xAxisData, legendData, tableInfo,
    toolTipsFormatter, height, lineSeries, barSeries, isCopilot,  toolTipsDateFormatter, chartDataId, yAxisConfig, isFullScreen } = props || {};
  const { name: metricsName, type: dataFormatType, data_key: metricDataKeyField, chat_type: metricChatType } = metricObj || {};
  const metricsDataKey = typeof metricDataKeyField === 'string' ? metricDataKeyField : metricDataKeyField?.data_key || '';
  const chatType = metricChatType || propsChatType;
  const [selected, setSelected] = useState<string>();
  const xAxisIsDateOrTime = DATE_OR_TIME_AXIS_TYPE.includes(xAxis?.[0]?.date_key || xAxis?.[0]);
  const xAxisFormat = xAxis?.[0]?.format || '';
  const weekDayArr = useWeekDayArr();
  const { formatMessage } = useIntl(); // 中英切换方法
  const { granularity = 'daily' } = filterVal || {};
  const timeType = TIME_TYPE_MAP?.[granularity] || 'Day';
  const { setItem } = useAgentStoreContext();

  const isMonth = timeType === 'Month';
  const isHour = timeType === 'hour';
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const barChartRef = useRef<any>();
  const lineChartRef = useRef<any>();

  // 获取当前激活的图表实例
  const getActiveChartInstance = useCallback(() => {
    if (selected === 'line') {
      return lineChartRef.current?.instance?.();
    }
    if (selected === 'bar' || selected === 'flat_bar') {
      return barChartRef.current?.instance?.();
    }
    return null;
  }, [selected]);

  useEffect(() => {
    if (chartDataId) {
      setItem(chartDataId, { getActiveChartInstance });
    }
  }, [chartDataId, getActiveChartInstance]);
  
  // 防止切换中英文，浮窗不会同步更新,用key处理会造成整个图表重新渲染
  useEffect(() => {
    barChartRef.current?.refreshChart();
    lineChartRef.current?.refreshChart();
  }, [isEn]);

  // 全屏状态变化时触发 window.resize 事件，让所有图表（含 PieChart）自适应
  useEffect(() => {
    !isFullScreen && window.dispatchEvent(new Event('resize'));
  }, [isFullScreen]);

  const commonChartOtherConfig = useCallback(
    (opt: EChartsOption,type?:string) => {
      let tempOpt = xAxisLabelFormat(opt, {
        xAxisLabelFormat: (value: string) => {
          return handleAxisFormat(value,xAxisFormat,  timeType, xAxisIsDateOrTime);
        },
      });
      if (xAxis?.[0]?.show_type !== 'all_tilt') {
        tempOpt = xAxisHighLight(tempOpt, {
          formatter: (params: { value: string; }) =>
            handleAxisFormat(params?.value, xAxisFormat, timeType, xAxisIsDateOrTime),
        });
      }
      // 使用封装的方法统一处理 yAxis
      tempOpt = setYAxisLabelFormatter(tempOpt, dataFormatType, (params) => {
        // 走 agentYAxisFormat 兼容 BMK 类型公共注册对负数不加单位的问题（如 -200K）
        return agentYAxisFormat({ value: params.value, formatRule: yAxisFormatter });
      });

      const maxMetricValue = metricMaxValueMap?.[metricsDataKey] || undefined;
      // const shouldSetMax = yAxisFormatter !== 'percentInt';
      const setMax = yAxisFormatter !== 'percentInt' ? 5 : 0.05;
      tempOpt = setYAxisMax(tempOpt, {
        shouldSetMax: true,
        maxSetVal: SMALL_VAL_METRIC?.includes(metricsDataKey)? 0.5 : setMax,
      }, maxMetricValue);

      // if(metricsDataKey === 'arpu') {
      //   tempOpt.yAxis.interval = 1;
      // }

      tempOpt = {
        ...tempOpt,
        tooltip: {
          ...tempOpt.tooltip,
          enterable: true, // 允许鼠标进入tooltip
        },
      };

      tempOpt = setXAxis(xAxis?.[0], tempOpt, isCopilot ? 45 : 50);
      if (type === 'flat_bar' && tempOpt.series) {
        tempOpt = setSeriesStack(tempOpt);
      }
      tempOpt = setSeriesEmphasis(tempOpt);

      // 应用 yAxis 配置（包含所有 ECharts yAxis 配置项）
      if (yAxisConfig) {
        tempOpt = applyYAxisConfig(tempOpt, yAxisConfig);
      }
      return tempOpt;
    },
    [dataFormatType, timeType, yAxisFormatter, yAxisConfig],
  );

  const commonTooltip = (params: any[] | any) => {
    // 兼容饼图：params 为单个对象（非数组）
    if (params && !Array.isArray(params)) {
      const { name, value, percent, color } = params || {};
      const formatted = agentDataFormat({ value, formatRule: yAxisFormatter });
      const pct = percent ? `(${percent})` : '';
      // 使用 getEchartTooltipText 统一饼图浮窗样式（与折线图/柱状图一致）
      return getEchartTooltipText({
        title: '',
        metricsName: '',
        data: [{ name, metricsNum: `${formatted}${pct}`, color }],
        hasDot: true,
      });
    }

    // 折线图/柱状图：params 为数组
    const paramArr = params as any[];
    // 只有当浮窗只有一条数据（lengend只有一个），且左侧名称和指标名称一致时，不显示指标名称
    const showMetricsName = !(paramArr?.length === 1 && paramArr?.[0]?.seriesName === metricsName);
    const isDateFormat = isMomentParsableString(paramArr?.[0]?.axisValue);
    const formattedParams = paramArr.map((item) => {
      item.axisValue = dateTimeFormat(item?.axisValue, toolTipsDateFormatter, '', { isStrictMode: true });
      return item;
    });

    const isSort = (yAxisConfig as any)?.inverse === true ? 'asc' : 'desc';

    return chartTooltiosFormatter(formattedParams,
      {
        weekDayArr: (isMonth || isHour || !isDateFormat) ? [] : weekDayArr,
        format: toolTipsFormatter,
        isSort,
        metricsName: showMetricsName ? metricsName : '',
        dataFormatFunc: agentDataFormat,
      });
  };

  // 根据 chatType 动态过滤 renderList
  const { renderList, defaultActiveTab,defaultSelected } = useMemo((): IRenderListResult => {
    // 未获取到指标则不渲染图表，不然会导致icon选中问题（默认值不是指标中chart_type的第一个）
    if (!metricObj) return {
      renderList: [],
      defaultActiveTab: 0
    };
    const list: IRenderListItem[] = [
      {
        type: 'line',
        svgType: 'chart-agent-line',
        content: formatMessage({ id: 'common_line_chart' }),
        component: <LineChart
          ref={lineChartRef}
          height={height}
          legendData={xAxisData.length > 0 ? legendData : []}
          xAxisData={xAxisData || []}
          series={xAxisData.length > 0 ? lineSeries : []}
          formatterFunc={commonTooltip}
          setOtherConfigFunc={commonChartOtherConfig}
        />
      },
      {
        type: 'bar',
        svgType: 'column-chart',
        content: formatMessage({ id: 'common_bar_chart' }),
        component: <BarChart
          ref={barChartRef}
          height={height}
          legendData={xAxisData?.length > 0 ? legendData : []}
          xAxisData={xAxisData || []}
          series={xAxisData?.length > 0 ? barSeries : []}
          formatterFunc={commonTooltip}
          isStackBar={false}
          setOtherConfigFunc={commonChartOtherConfig}
        />
      },
      {
        type: 'flat_bar',
        svgType: 'flat_bar_chart',
        content: formatMessage({ id: 'dba_common_flat_bar_chart' }),
        component: <BarChart
          ref={barChartRef}
          height={height}
          legendData={xAxisData?.length > 0 ? legendData : []}
          xAxisData={xAxisData || []}
          wrapperDom={document.getElementsByClassName('dbh-agent-copilot')?.[0]}
          series={xAxisData?.length > 0 ? barSeries : []}
          formatterFunc={commonTooltip}
          isStackBar={false}
          setOtherConfigFunc={(opt:EChartsOption)=>commonChartOtherConfig(opt,'flat_bar')}
        />
      },
      {
        type: 'table',
        svgType: 'sheet',
        content: formatMessage({ id: 'common_table' }),
        component: (
          <AITable
            tableInfo={tableInfo}
            isShowTableHeaderEllipsis={true}
            height={height}
          />
        ),
      },
      {
        type: 'pie',
        svgType: 'chart-agent-pie',
        content: formatMessage({ id: 'common_pie_chart' }),
        component: <PieChart
          height={height}
          legendData={legendData}
          chartData={getPieData(lineSeries)}
          formatterFunc={commonTooltip}
        />,
      },
    ];
    // 3. 优化后的逻辑
    if (typeof chatType === 'string') {
      const config = CHART_MODE_CONFIG[chatType as ChatMode] || {
        allowedTypes: ['line', 'bar', 'pie', 'table'],
        defaultIndex: 0
      };
      return {
        renderList: list.filter(item => config.allowedTypes.includes(item.type as ChartType)),
        defaultActiveTab: config.defaultIndex,
        defaultSelected: (config.allowedTypes)?.[0]
      };
    }
    // 4. 处理数组类型chatType,按照chatType数组顺序排序
    const sortedList = chatType?.map(type => list.find(item => item?.type === type)).filter(Boolean) as IRenderListItem[]; // 过滤掉未找到的项
    return {
      renderList: sortedList?.filter(item => chatType.includes(item.type)),
      defaultActiveTab: sortedList?.findIndex(item => item.type === chatType[0]) || 0,
      defaultSelected: chatType?.[0]
    };
  }, [componentData, commonChartOtherConfig, weekDayArr, chatType]);

  const selectOption = useMemo(() => {
    if (!isCopilot) return null;
    const result =  (renderList || [])?.map((item: IRenderListItem)=>{
      return {
        value: item?.type,
        text: item?.content,
        icon: <SvgIcon className='dba-chart-select-icon' type={item?.svgType} />,
      };
    });
    return [...result,  {
      value: 'fullScreen',
      text: formatMessage({ id: 'dba_full_screen' }),
      icon: <SvgIcon className='dba-chart-select-icon'
        type={isFullScreen ? "clonse" : "fullscreen" }/>,
    },
    {
      value: 'download',
      text: formatMessage({ id: 'dba_download' }),
      icon: <SvgIcon className='dba-chart-select-icon' type='download' />,
    },
    ];
  }, [isCopilot, renderList]);

  useEffect(() => { 
    setSelected(defaultSelected);
  }, [defaultSelected]);


  return {
    renderList,
    defaultActiveTab,
    selected,
    setSelected,
    selectOption,
    getActiveChartInstance
  };
};